import ScrollReveal from '@/components/feature/ScrollReveal';
import AnimatedCounter from '@/components/feature/AnimatedCounter';
import Text3DHover from '@/components/feature/Text3DHover';

export default function AboutSection() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About Lucky Holidays - Premium Travel & Tourism Company',
    description: 'Learn about Lucky Holidays India Private Limited, a premium travel agency offering international tours, domestic holidays, visa assistance, flight bookings, and customized travel packages.',
    url: 'https://www.luckyholidays.in/#about',
    mainEntity: {
      '@type': 'TravelAgency',
      name: 'Lucky Holidays India Private Limited',
      alternateName: 'Lucky Holidays',
      url: 'https://www.luckyholidays.in/',
      logo: 'https://www.luckyholidays.in/lucky-holidays-logo.webp',
      image: 'https://www.luckyholidays.in/lucky-holidays-logo.webp',
      description: 'Premium Travel & Tourism Company offering international and domestic tours, customized holidays, family vacations, group tours, corporate travel, visa assistance, flights, hotels, and cruise holidays.',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Pandian Street, Chinna Thirupathi, Salem',
        addressLocality: 'Hosur',
        addressRegion: 'Tamil Nadu',
        postalCode: '635109',
        addressCountry: 'IN',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: '12.7409',
        longitude: '77.8253',
      },
      telephone: '+91-90035-60700',
      email: 'lucky@luckyholidays.in',
      priceRange: '$$$',
      areaServed: {
        '@type': 'GeoCircle',
        geoMidpoint: {
          '@type': 'GeoCoordinates',
          latitude: '12.7409',
          longitude: '77.8253',
        },
        geoRadius: '500000',
      },
      sameAs: [
        'https://www.instagram.com/luckyholidays24x7',
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Travel Services',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'International Tours' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Domestic Tours' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Customized Holiday Packages' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Family Holidays' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Group Tours' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Corporate Travel Solutions' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Visa Assistance' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Flight Ticket Bookings' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Hotel Reservations' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Cruise Holidays' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Honeymoon Packages' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Travel Insurance' } },
        ],
      },
    },
  };

  return (
    <section id="about" className="py-16 md:py-24 bg-background-50" itemScope itemType="https://schema.org/AboutPage">
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <div className="w-full px-4 md:px-8 lg:px-16 3xl:max-w-[1800px] 3xl:mx-auto tv:max-w-[3000px] tv:mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <ScrollReveal direction="left" className="order-2 lg:order-1">
            <span className="inline-block px-4 py-1.5 bg-primary-50 text-primary-700 text-xs font-semibold tracking-widest uppercase rounded-full mb-4">
              About Us
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl 3xl:text-6xl font-bold text-foreground-900 mb-6">
              Crafting Unforgettable Journeys Since Inception
            </h2>
            <div className="space-y-4 text-foreground-600 text-sm md:text-base leading-relaxed">
              <Text3DHover variant="lift">
                <p>
                  <strong className="text-foreground-900" itemProp="name">Lucky Holidays India Private Limited</strong> is a premium
                  Travel &amp; Tourism Company dedicated to creating unforgettable travel experiences worldwide. We
                  specialize in <a href="#services" className="text-primary-600 hover:underline">International and Domestic Tours</a>,{' '}
                  <a href="#services" className="text-primary-600 hover:underline">Customized Holidays</a>,{' '}
                  <a href="#services" className="text-primary-600 hover:underline">Family Vacations</a>,{' '}
                  <a href="#services" className="text-primary-600 hover:underline">Group Tours</a>,{' '}
                  <a href="#services" className="text-primary-600 hover:underline">Corporate Travel Solutions</a>,{' '}
                  <a href="#services" className="text-primary-600 hover:underline">Visa Assistance</a>,{' '}
                  <a href="#services" className="text-primary-600 hover:underline">Flight Ticket Bookings</a>,{' '}
                  <a href="#services" className="text-primary-600 hover:underline">Hotel Reservations</a> and{' '}
                  <a href="#services" className="text-primary-600 hover:underline">Cruise Holidays</a>.
                </p>
              </Text3DHover>
              <Text3DHover variant="glow">
                <p>
                  Our mission is to provide{' '}
                  <strong className="text-foreground-900">personalized, seamless and memorable travel experiences</strong>{' '}
                  that inspire our clients to explore the world with confidence and comfort. Based in{' '}
                  <a href="https://www.google.com/maps/search/Hosur,+Tamil+Nadu,+India" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">Hosur, Tamil Nadu</a>, we serve travelers across India with premium travel services.
                </p>
              </Text3DHover>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-background-100 rounded-xl card-hover-lift transition-all duration-300 group cursor-default">
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 group-hover-icon-bounce">
                  <i className="ri-global-line mr-0.5"></i><AnimatedCounter end={50} suffix="+" />
                </div>
                <div className="text-xs text-foreground-600 mt-1">Countries Covered</div>
              </div>
              <div className="text-center p-4 bg-background-100 rounded-xl card-hover-lift transition-all duration-300 group cursor-default">
                <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500 group-hover-icon-bounce">
                  <i className="ri-user-heart-line mr-0.5"></i><AnimatedCounter end={5000} suffix="+" />
                </div>
                <div className="text-xs text-foreground-600 mt-1">Happy Travelers</div>
              </div>
              <div className="text-center p-4 bg-background-100 rounded-xl card-hover-lift transition-all duration-300 group cursor-default">
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 group-hover-icon-bounce">
                  <i className="ri-map-pin-line mr-0.5"></i><AnimatedCounter end={500} suffix="+" />
                </div>
                <div className="text-xs text-foreground-600 mt-1">Destinations</div>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="right" className="order-1 lg:order-2 relative">
            <div className="relative rounded-2xl overflow-hidden aspect-[4/5] img-zoom-on-hover">
              <img
                alt="Lucky Holidays premium travel agency office in Hosur Tamil Nadu with world map and travel consultants"
                title="Lucky Holidays India Private Limited - Premium Travel Agency Hosur"
                className="w-full h-full object-cover object-top"
                src="https://readdy.ai/api/search-image?query=Elegant%20luxury%20travel%20agency%20office%20interior%20with%20world%20map%20wall%20art%2C%20premium%20furniture%2C%20warm%20lighting%2C%20professional%20travel%20consultants%2C%20sophisticated%20corporate%20atmosphere%2C%20minimalist%20design%2C%20plants%2C%20modern%20workspace%2C%20travel%20brochures&width=800&height=1000&seq=lucky-about-img&orientation=portrait"
                loading="lazy"
              />
            </div>
            <div className="absolute -bottom-4 -left-4 bg-background-50 rounded-xl p-4 border border-background-200/70 max-w-[200px] card-hover-lift">
              <div className="flex items-center gap-2 mb-1">
                <i className="ri-shield-star-line text-accent-500 text-lg animate-icon-pulse"></i>
                <span className="text-xs font-semibold text-foreground-900">Trusted Partner</span>
              </div>
              <p className="text-[11px] text-foreground-600 leading-relaxed">
                Recognized by leading travel associations for service excellence.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}